import {
  type TileMedia,
  type TileMediaSpec,
  type TileMediaTracking,
} from '../../MediaCard';
import {shouldNotLogBlobFetchSuccess} from '../fetch/update';

/**
 * Flag-off path: infer cache hit from timestamps stamped before this tile mount.
 * Used when {@link TileMediaTracking.isCacheHit} is unset (imperative pipeline).
 */
export function isTimestampHeuristicCacheHit({
  tracking,
  tileMedia,
  tileMediaSpec,
  componentCreatedPerformanceMs,
  disableVideoStream,
}: {
  tracking: TileMediaTracking;
  tileMedia: TileMedia;
  tileMediaSpec: TileMediaSpec;
  componentCreatedPerformanceMs: number;
  disableVideoStream: boolean;
}): boolean {
  const {
    availableFilesReceivedPerformanceMs,
    directUrlReceivedPerformanceMs,
    blobFetchCompletePerformanceMs,
    dataCachedPerformanceMs,
  } = tracking;

  return (
    availableFilesReceivedPerformanceMs < componentCreatedPerformanceMs ||
    directUrlReceivedPerformanceMs < componentCreatedPerformanceMs ||
    (blobFetchCompletePerformanceMs < componentCreatedPerformanceMs &&
      !shouldNotLogBlobFetchSuccess(
        tileMedia,
        tileMediaSpec,
        disableVideoStream
      )) ||
    dataCachedPerformanceMs < componentCreatedPerformanceMs
  );
}

/**
 * Flag-on `isCacheHit`: AvailableFiles metadata was served from Apollo cache
 * (no initial `NetworkStatus.loading` without cached data). Blob warmth is
 * reflected in `blobFetch` stage duration, not this flag.
 */
export function isAvailableFilesApolloCacheHit({
  hasAvailableFilesData,
  observedInitialNetworkLoad,
}: {
  hasAvailableFilesData: boolean;
  observedInitialNetworkLoad: boolean;
}): boolean {
  return hasAvailableFilesData && !observedInitialNetworkLoad;
}

export function resolveTileMediaCacheHit({
  tracking,
  tileMedia,
  tileMediaSpec,
  componentCreatedPerformanceMs,
  disableVideoStream,
}: {
  tracking: TileMediaTracking;
  tileMedia: TileMedia;
  tileMediaSpec: TileMediaSpec;
  componentCreatedPerformanceMs: number;
  disableVideoStream: boolean;
}): boolean {
  if (tracking.isCacheHit != null) {
    return tracking.isCacheHit;
  }

  return isTimestampHeuristicCacheHit({
    tracking,
    tileMedia,
    tileMediaSpec,
    componentCreatedPerformanceMs,
    disableVideoStream,
  });
}
